import { Injectable } from '@angular/core';
import {
  ContractualOperationValidationOptions,
  ContractualTimeGroup,
  ErrorType,
  StartAndEnd,
  ValidationResult
} from '../../../../models/projects/validation.models';
import {
  DailyLogOperationInstance,
} from '../../../../models/projects/operation.models';
import { DateService } from '../../../date/date.service';
import { DateParserService } from '../../../daily-log/daily-log-time/date-parser-service';
import { OperationService } from '../../operation/operation.service';
import { cloneDeep } from 'lodash';
import { PtLuxonDate } from '../../../../models/projects/date.models';
import { TimeZoneService } from '../../../time-zone/time-zone.service';
import { DateTime } from 'luxon';
import { DailyLogService } from '../../../daily-log/daily-log.service';
import { forEach } from '@angular/router/src/utils/collection';

@Injectable()
export class ContractualOperationValidatorService {

  second: number = 1000;

  constructor(
    private dateService: DateService,
    private dateParserService: DateParserService,
    private operationService: OperationService,
    private timeZoneService: TimeZoneService,
    private dailyLogService: DailyLogService
  ) {
  }

  async checkContractualOperation(options: ContractualOperationValidationOptions, validationResult: ValidationResult) {
    const isMidnight = (options.mostRecentTime.dateTime.toUTC().hour === 0 && options.mostRecentTime.dateTime.minute === 0);

    const previousTime = await this.findPreviousContractualTime(options.instance.startTime, options.instance);
    // this.operationService.latestOperationTime(options.previousOperation);
    if (!!previousTime) {
      options.difference =
        this.dateService.millisecondDifference(options.instance.startTime.dateTime,
          previousTime.endTime.dateTime);
    }

    if (!!previousTime) {
      if (options.difference >= this.second && !isMidnight) {
        validationResult.addError(`Saving this operation will cause a gap between the previous time (Operation:
          ${previousTime.operationInstance.projectSpecificOperation.operationSummary.name}, end time:
          ${this.dateService.setTimeStringFromPtLuxonDate(previousTime.endTime)}) and your operation start time (Operation:
          ${options.instance.projectSpecificOperation.name
          ? options.instance.projectSpecificOperation.name
          : options.instance.projectSpecificOperation.operationSummary.name}
              , start time: ${options.instance.startTimeToShow})`, ErrorType.Warning);
      }
    }

    if (options.difference >= this.second && isMidnight) {
      validationResult.addError(`Saving this operation will cause a gap between the start of the day (00:00) and your operation start time
      (Operation: ${options.instance.projectSpecificOperation.name
        ? options.instance.projectSpecificOperation.name
        : options.instance.projectSpecificOperation.operationSummary.name},
         start time: ${options.instance.startTimeToShow})`, ErrorType.Warning);
    }

    if (options.instance.projectSpecificOperation.operationSummary.classificationName !== 'Delay') {
      const overlapDates = await this.checkPartialDelayOverlap(options.instance); //

      if (overlapDates != null && overlapDates.length !== 0) {
        for (const element of overlapDates) {
          if ((!!element.operationInstance
            && element.operationInstance.projectSpecificOperation.operationSummary.classificationName === 'Delay')
            || (!!element.previousOperation
              && element.previousOperation.projectSpecificOperation.operationSummary.classificationName === 'Delay')) {
            return;
          }

          if (!!element.operationInstance.projectSpecificOperation) {
            validationResult.addError(`Saving this operation will cause an overlap between a previous contractual time (Operation:
          ${element.operationInstance.projectSpecificOperation.name
              ? element.operationInstance.projectSpecificOperation.name
              : element.operationInstance.projectSpecificOperation.operationSummary.name}, start time:
          ${this.dateService.setTimeStringFromPtLuxonDate(element.startTime)}, end time:
          ${this.dateService.setTimeStringFromPtLuxonDate(element.endTime)}) and your operation (Operation:
          ${options.instance.projectSpecificOperation.name
              ? options.instance.projectSpecificOperation.name
              : options.instance.projectSpecificOperation.operationSummary.name}, start time:
          ${options.instance.startTimeToShow}, end time:
          ${options.instance.endTimeToShow})`, ErrorType.Warning);
          }
        }
      }

      const onGoingOperations = await this.detectOpenEndedOperations();

      if (onGoingOperations.length > 0) {
        validationResult.addError(`You currently have ${onGoingOperations.length} on going contractual
      ${onGoingOperations.length === 1 ? ' operation' : ' operations'},
      adding a new one before completing ${onGoingOperations.length === 1 ? 'this' : 'these'} could result in an overlap.`,
          ErrorType.Warning);
      }
    }

    const startOverlaps = await this.checkDelayStartOverlap(options.instance);

    if (startOverlaps != null && startOverlaps.length > 0) {
      for (const overlap of startOverlaps) {
        validationResult.addError(`Your operation start time is overlapping a delay (Delay:
          ${overlap.operationInstance.projectSpecificOperation.name
          ? overlap.operationInstance.projectSpecificOperation.name
          : overlap.operationInstance.projectSpecificOperation.operationSummary.name}, start time:
          ${this.dateService.setTimeStringFromPtLuxonDate(overlap.startTime)}, end time:
          ${this.dateService.setTimeStringFromPtLuxonDate(overlap.endTime)}). This cannot happen.`, ErrorType.Error);
      }
    }
  }

  async findPreviousContractualTime(date?: PtLuxonDate, instance?: DailyLogOperationInstance): Promise<StartAndEnd> {
    const contractualTimes = await this.getOtherContractualTimes(instance, date, null, null);

    let chosenTime = null;
    let latestTime = null;

    if (!!date) {
      latestTime = cloneDeep(date.dateTime);
    }

    if (!date) {
      latestTime = null;
    }

    if (!!latestTime) {
      latestTime.setUTCHours(0);
      latestTime.setUTCMinutes(0);
      latestTime.setSeconds(0);
      latestTime.setMilliseconds(0);
    }

    contractualTimes.forEach(time => {

      time.endTime = PtLuxonDate.assignPtLuxonDate(time.endTime);
      time.startTime = PtLuxonDate.assignPtLuxonDate(time.startTime);

      if (!!time.endTime.dateTime && time.endTime.dateTime > latestTime) {
        latestTime = cloneDeep(time.endTime.dateTime);
        chosenTime = cloneDeep(time);
      }
    });

    return chosenTime;
  }

  async findOverlap(newOperation: DailyLogOperationInstance): Promise<StartAndEnd[]> {
    let operations = cloneDeep(
      await this.operationService.getAllProjectSpecificOperationInstances(newOperation.id)
    ) as DailyLogOperationInstance[];

    operations = operations
      .filter(p => p.projectSpecificOperation.operationSummary.classificationName !== 'Delay')
      .filter(p => !p.isConcurrentOperation);

    const overlapDates = [];

    let newEndTime = await this.dateService.setPtLuxonDateFromUserEnteredString(newOperation.endTimeToShow, newOperation.endTime.dateTime);

    if (!!newOperation.cancelledTime) {
      newEndTime = new PtLuxonDate(newOperation.cancelledTime);
    }

    const newStartTime = await this.dateService.setPtLuxonDateFromUserEnteredString(newOperation.startTimeToShow, newOperation.startTime.dateTime);

    if (newEndTime.dateTime == null || newStartTime.dateTime == null) {
      return;
    }

    operations.forEach(element => {

      if (element.id === newOperation.id) {
        return;
      }

      if (element.startTime.dateTime == null || element.endTime.dateTime == null) {
        return;
      }

      let overlapping = this.operationService.operationInTimeExclusive(
        element.startTime.dateTime,
        element.endTime.dateTime,
        newStartTime.dateTime,
        newEndTime.dateTime);

      if (!overlapping) {
        overlapping = this.operationService.operationInTimeExclusive(
          newStartTime.dateTime,
          newEndTime.dateTime,
          element.startTime.dateTime,
          element.endTime.dateTime);
      }

      /*const beforeOldOp = (newStartTime.dateTime
        <= element.operation.startTime.dateTime && newEndTime.dateTime
        <= element.operation.startTime.dateTime);

      const afterOldOp = (newStartTime.dateTime
        >= element.operation.endTime.dateTime && (newEndTime.dateTime
          >= element.operation.endTime.dateTime
          || newEndTime.dateTime <= element.operation.startTime.dateTime));*/

      if (overlapping) {
        const overlapDate = new StartAndEnd();
        overlapDate.startTime = element.startTime;
        overlapDate.endTime = element.endTime;
        overlapDate.operationInstance.projectSpecificOperation = element.projectSpecificOperation;
        overlapDate.previousOperation = newOperation;
        overlapDate.previousOperatioInstanceId = newOperation.id;
        overlapDate.operationInstanceId = element.id;
        overlapDates.push(overlapDate);
        return;
      }
    });

    return overlapDates;
  }

  async detectOpenEndedOperations(): Promise<DailyLogOperationInstance[]> {

    const operations = await this.operationService
      .getAllProjectSpecificOperationInstances(null);

    const relevantOperations = operations
      .filter(p => p.endTime.dateTime == null)
      .filter(p => !p.isConcurrentOperation);

    return relevantOperations;
  }

  async checkDelayStartOverlap(newOperation: DailyLogOperationInstance): Promise<StartAndEnd[]> {

    if (newOperation.isInterruption) {
      return;
    }

    const delays = cloneDeep(await this.operationService.getDelays());

    const overlapDates = [];

    const newStartTime = await this.dateService.setPtLuxonDateFromUserEnteredString(newOperation.startTimeToShow, newOperation.startTime.dateTime);

    if (this.operationService.operationConcurrentAtTime(newOperation, newStartTime.dateTime)) {
      return;
    }

    if (newStartTime.dateTime == null) {
      return;
    }

    delays.forEach(element => {
      element.startTime = PtLuxonDate.assignPtLuxonDate(element.startTime);
      element.endTime = PtLuxonDate.assignPtLuxonDate(element.endTime);

      if (element.id === newOperation.id) {
        return;
      }

      if (element.startTime.dateTime == null || element.endTime.dateTime == null) {
        return;
      }

      const duringDelay = (newStartTime.dateTime
        >= element.startTime.dateTime && newStartTime.dateTime
        < element.endTime.dateTime);

      if (duringDelay) {
        const overlapDate = new StartAndEnd();
        overlapDate.startTime = element.startTime;
        overlapDate.endTime = element.endTime;
        overlapDate.operationInstance = element;
        overlapDate.operationInstanceId = element.id;
        overlapDates.push(overlapDate);
        return;
      }
    });

    return overlapDates;
  }

  async checkPartialDelayOverlap(newOperation: DailyLogOperationInstance, date?: PtLuxonDate): Promise<StartAndEnd[]> {
    const overlaps = [] as StartAndEnd[];
    const contractualTimes = await this.getOtherContractualTimes(newOperation, date, null);
    const newOperationContractualTimes = (await this.getTimesForOperation(newOperation)).times;

    newOperationContractualTimes.forEach(newTime => {
      newTime.endTime = PtLuxonDate.assignPtLuxonDate(newTime.endTime);
      newTime.startTime = PtLuxonDate.assignPtLuxonDate(newTime.startTime);

      contractualTimes.forEach(oldTime => {
        oldTime.endTime = PtLuxonDate.assignPtLuxonDate(oldTime.endTime);
        oldTime.startTime = PtLuxonDate.assignPtLuxonDate(oldTime.startTime);

        let overlapping = this.operationService.operationInTimeExclusive(
          newTime.startTime.dateTime,
          newTime.endTime.dateTime,
          oldTime.startTime.dateTime,
          oldTime.endTime.dateTime);

        if (!overlapping) {
          overlapping = this.operationService.operationInTimeExclusive(
            oldTime.startTime.dateTime,
            oldTime.endTime.dateTime,
            newTime.startTime.dateTime,
            newTime.endTime.dateTime);
        }


        if (!overlapping) {
          let startMatch = false;

          if (!oldTime.startTime.dateTime || !newTime.startTime.dateTime) {
            startMatch = oldTime.endTime.dateTime === newTime.startTime.dateTime;
          } else {
            startMatch = Math.abs(oldTime.startTime.dateTime.toMillis() - newTime.startTime.dateTime.toMillis()) < 1000;
          }

          let endMatch = false;

          if (!oldTime.endTime.dateTime || !newTime.endTime.dateTime) {
            endMatch = oldTime.endTime.dateTime === newTime.endTime.dateTime;
          } else {
            endMatch = Math.abs(oldTime.endTime.dateTime.toMillis() - newTime.endTime.dateTime.toMillis()) < 1000;
          }

          overlapping = startMatch && endMatch;
        }

        if (overlapping) {
          const overlapTime = new StartAndEnd();
          overlapTime.startTime = cloneDeep(oldTime.startTime);
          overlapTime.endTime = cloneDeep(oldTime.endTime);
          overlapTime.operationInstance = cloneDeep(oldTime.operationInstance);
          overlapTime.operationInstanceId = cloneDeep(oldTime.operationInstanceId);
          overlapTime.previousOperation = cloneDeep(newTime.operationInstance);
          overlapTime.previousOperatioInstanceId = cloneDeep(newTime.operationInstanceId);
          overlaps.push(overlapTime);
        }
      });
    });

    return overlaps;
  }

  async getOtherContractualTimesUtc(newOperation?: DailyLogOperationInstance, date?: PtLuxonDate, concurrent?: boolean): Promise<StartAndEnd[]> {
    let contractualTimes: StartAndEnd[] = [];
    let id = null;
    let operations = null as DailyLogOperationInstance[];

    if (!!newOperation) {
      id = newOperation.id;
    }

    if (!!date) {
      operations = cloneDeep(
        (await this.operationService.getAllProjectSpecificOperationInstancesForDay(date))
          .filter(p => p.id !== id));
    } else {
      operations = cloneDeep(
        await this.operationService.getAllProjectSpecificOperationInstances(id)
      ) as DailyLogOperationInstance[];
    }

    // todo - not needed remove
    // operations = this.dateParserService.resolveDates(operations);

    operations = operations.filter(p => !p.isInterruption);

    for (let i = 0; i < operations.length; i++) {
      if (!concurrent) {
        const timeGroup = await this.getTimesForOperation(operations[i]);
        contractualTimes = contractualTimes.concat(timeGroup.times);
      } else {
        const timeGroup = await this.getTimesForOperation(operations[i], true);
        contractualTimes = contractualTimes.concat(timeGroup.times);
      }
    }

    return contractualTimes;
  }

  async getOtherContractualTimes(
    newOperation?: DailyLogOperationInstance,
    date?: PtLuxonDate,
    concurrent?: boolean,
    operations?: DailyLogOperationInstance[],
    dailyLogDate?: PtLuxonDate
  ): Promise<StartAndEnd[]> {
    let contractualTimes: StartAndEnd[] = [];
    let filteredOperations: DailyLogOperationInstance[] = [];
    const newOperationId = newOperation ? newOperation.id : null;

    if (!operations) {
      if (date) {
        filteredOperations = (await this.operationService.getAllProjectSpecificOperationInstancesForDay(date, null, null))
            .filter(p => p.id !== newOperationId);
      } else {
        filteredOperations = await this.operationService.getAllProjectSpecificOperationInstances(newOperationId);
      }
    } else {
      filteredOperations = operations.filter(p => p.id !== newOperationId);
    }

    filteredOperations = filteredOperations.filter(p => !p.isInterruption);

    for (let i = 0; i < filteredOperations.length; i++) {
      if (!concurrent) {
        const timeGroup = await this.getTimesForOperation(filteredOperations[i], null, null, dailyLogDate);
        contractualTimes = contractualTimes.concat(timeGroup.times);
      } else {
        const timeGroup = await this.getTimesForOperation(filteredOperations[i], true, null, dailyLogDate);
        contractualTimes = contractualTimes.concat(timeGroup.times);
      }
    }

    return contractualTimes;
  }

  // This method will split up an operation's timeSpan into each chunk of
  // contractual/concurrent (Depending on the second parameter) time within it.
  // also splits based on pauses
  async getTimesForOperation(
    operationInstance: DailyLogOperationInstance,
    concurrentTimes?: boolean,
    endOfDay?: PtLuxonDate,
    dailyLogDate?: PtLuxonDate
  ): Promise<ContractualTimeGroup> {
    if (!endOfDay || !endOfDay.dateTime) {
      if (!dailyLogDate || !dailyLogDate.dateTime) {
        dailyLogDate = await this.dailyLogService.getCurrentLogDay();
      }

      endOfDay = await this.dateService.getEndOfDayLocally(dailyLogDate.dateTime);
    }

    let currentlyContractual = false;
    let contractualTimes = [] as StartAndEnd[];
    const contractualTimeSpan = new StartAndEnd();

    contractualTimeSpan.operationInstance = operationInstance;
    contractualTimeSpan.operationInstanceId = operationInstance
      ? operationInstance.id
        ? operationInstance.id
        : operationInstance.localId
      : null;

    contractualTimeSpan.startTime = operationInstance.startTime;

    if (!operationInstance.isConcurrentOperation) {
      currentlyContractual = true;
    }

    if (operationInstance.switchTimes != null && operationInstance.switchTimes.length > 0) {
      let viableSwitchTimes = operationInstance.switchTimes
        .sort((function (a, b) {
          // sort by date asc
          return a.date.dateTime.toMillis() - b.date.dateTime.toMillis();
        }));


      /*if (!!operationInstance.endTime) {
        viableSwitchTimes = viableSwitchTimes.filter(p => p.date < operationInstance.endTime);
      }*/

      if (!!operationInstance.cancelledTime) {
        viableSwitchTimes = viableSwitchTimes.filter(p => p.date.dateTime < operationInstance.cancelledTime);
      }

      for (let i = 0; i < viableSwitchTimes.length; i++) {
        const switchTime = viableSwitchTimes[i];

        if (switchTime.isConcurrent && currentlyContractual) {
          currentlyContractual = false;
          if (!concurrentTimes) {
            if (!!contractualTimeSpan.startTime) {
              contractualTimeSpan.endTime = cloneDeep(switchTime.date);
              contractualTimes.push(cloneDeep(contractualTimeSpan));
              // contractualTimeSpan = new StartAndEnd();
            }
          } else {
            currentlyContractual = false;
            contractualTimeSpan.startTime = switchTime.date;
            contractualTimeSpan.operationInstance = operationInstance;
            contractualTimeSpan.operationInstanceId = operationInstance
              ? operationInstance.id
                ? operationInstance.id
                : operationInstance.localId
              : null;
          }
        }

        if (!switchTime.isConcurrent && !currentlyContractual) {
          if (!concurrentTimes) {
            currentlyContractual = true;
            contractualTimeSpan.startTime = switchTime.date;
            contractualTimeSpan.operationInstance = operationInstance;
            contractualTimeSpan.operationInstanceId = operationInstance
              ? operationInstance.id
                ? operationInstance.id
                : operationInstance.localId
              : null;
          } else {
            if (!!contractualTimeSpan.startTime) {
              contractualTimeSpan.endTime = switchTime.date;
              contractualTimes.push(contractualTimeSpan);
              // contractualTimeSpan = new StartAndEnd();
            }
          }
        }
      }
    }

    if (currentlyContractual === !concurrentTimes) {
      if (!!contractualTimeSpan.startTime) {
        if (operationInstance.cancelledTime) {
          contractualTimeSpan.endTime = new PtLuxonDate(operationInstance.cancelledTime);
        } else {
          contractualTimeSpan.endTime = operationInstance.endTime;
        }

        contractualTimes.push(contractualTimeSpan);
        // contractualTimeSpan = new StartAndEnd();
        currentlyContractual = !currentlyContractual;
      }
    }

    if (contractualTimes.length === 0 && operationInstance.isConcurrentOperation === !!concurrentTimes) {
      // contractualTimeSpan = new StartAndEnd();
      contractualTimeSpan.operationInstance = operationInstance;
      contractualTimeSpan.operationInstanceId = operationInstance
        ? operationInstance.id
          ? operationInstance.id
          : operationInstance.localId
        : null;
      contractualTimeSpan.startTime = operationInstance.startTime;

      if (operationInstance.cancelledTime) {
        contractualTimeSpan.endTime = new PtLuxonDate(operationInstance.cancelledTime);
      } else {
        contractualTimeSpan.endTime = operationInstance.endTime;
      }

      contractualTimes.push(contractualTimeSpan);
    }

    contractualTimes = cloneDeep(await this.updateContractualTimesWithPauseTimes(cloneDeep(operationInstance), cloneDeep(contractualTimes), endOfDay));

    for (let i = 0; i < contractualTimes.length; i++) {
      if ((!contractualTimes[i].endTime || !contractualTimes[i].endTime.dateTime) && endOfDay) {
        contractualTimes[i].endTime = endOfDay;
      }
    }

    const timeGroup = new ContractualTimeGroup();
    timeGroup.isContractual = currentlyContractual;
    timeGroup.times = contractualTimes;
    timeGroup.operationInstance = operationInstance;

    return timeGroup;
  }

  // Account for gaps caused by the operation being paused by a delay. Typically not an issue as
  // usually contractual ops are paused by delays, but if paused by an interruption, we need to check gaps
  // loop through contractual times, then inner loop through pause times
  // if pause intersects, set contractual end to be pause start
  // then add new contractual time* starting from pause end, and ending at old contractual end
  // *if pause has no end, or pause ends after day ends, don't add new contractual time
  // todo - this method only works if the pause is entirely inside the contractual time, need to redo to handle other cases
  private async updateContractualTimesWithPauseTimes(
    operationInstance: DailyLogOperationInstance,
    contractualTimes: StartAndEnd[],
    endOfDay?: PtLuxonDate): Promise<StartAndEnd[]> {

    contractualTimes.forEach(e => {
      if (e.endTime === null || e.endTime.dateTime === null) {
        e.endTime = cloneDeep(endOfDay);
      }
    });

    if (!!operationInstance.pausedTimes) {
      // keep looping through contractual times until all pauses have been accounted for
      const newContractualTimes = this.loopThroughContractualTimes(contractualTimes, operationInstance, endOfDay);
      contractualTimes = contractualTimes.concat(newContractualTimes);
    }

    return contractualTimes;
  }

  private loopThroughContractualTimes(contractualTimes: StartAndEnd[], operationInstance: DailyLogOperationInstance, endOfDay: PtLuxonDate): StartAndEnd[] {
    const newContractualTimes: StartAndEnd[] = [];
    for (let i = 0; i < contractualTimes.length; i++) {
      for (let j = 0; j < operationInstance.pausedTimes.length; j++) {
        let overlapping = this.operationService.operationInTimeExclusive(
          contractualTimes[i].startTime.getDate(),
          contractualTimes[i].endTime.getDate(),
          operationInstance.pausedTimes[j].startDate.getDate(),
          operationInstance.pausedTimes[j].endDate.getDate());

        if (!overlapping) {
          overlapping = this.operationService.operationInTimeExclusive(
            operationInstance.pausedTimes[j].startDate.getDate(),
            operationInstance.pausedTimes[j].endDate.getDate(),
            contractualTimes[i].startTime.getDate(),
            contractualTimes[i].endTime.getDate());
        }

        if (!overlapping) {
          continue;
        }

        if (operationInstance.pausedTimes[j].startDate.getDate() > contractualTimes[i].startTime.getDate()
          && operationInstance.pausedTimes[j].endDate != null
          && operationInstance.pausedTimes[j].endDate.dateTime != null
          && operationInstance.pausedTimes[j].endDate.getDate() < contractualTimes[i].endTime.getDate()) {

          if (operationInstance.pausedTimes[j].endDate == null || operationInstance.pausedTimes[j].endDate.dateTime == null) {
            continue;
          }

          if (operationInstance.pausedTimes[j].endDate !== null
            && operationInstance.pausedTimes[j].endDate.dateTime !== null
            && endOfDay != null
            && operationInstance.pausedTimes[j].endDate.dateTime.toMillis() > endOfDay.dateTime.toMillis()) {
            continue;
          }

          const newContractualTime = cloneDeep(contractualTimes[i]) as StartAndEnd;
          newContractualTime.startTime = cloneDeep(operationInstance.pausedTimes[j].endDate);
          newContractualTimes.push(cloneDeep(newContractualTime));

          // important that this line comes after the above clone for newContractualTime
          contractualTimes[i].endTime = cloneDeep(operationInstance.pausedTimes[j].startDate);
        }
      }
    }

    return newContractualTimes;
  }
}




// WEBPACK FOOTER //
// ./src/services/operations/operation-validation/contractual-operation-validator-service/contractual-operation-validator.service.ts